/**
 * Sprite Renderer Component
 *
 * Renders a 2D sprite with color tinting and transform.
 */

import {
  component,
  displayName,
  icon,
  color,
  category,
  requireComponent,
  executeInEditor,
  allowMultiple,
  field,
  customEditor,
} from "../attributes";
@component("core.SpriteRenderer")
@customEditor("SpriteRendererEditor")
@displayName("Sprite Renderer")
@icon("Image")
@color("#ff6b6b")
@category("Rendering")
@requireComponent(["core.Transform"])
@executeInEditor(true)
@allowMultiple(false)
export class SpriteRenderer {
  @field({ type: "asset", assetType: "texture", label: "Sprite", tooltip: "Texture asset to render" })
  sprite: string = "";

  @field({ type: "asset", assetType: "material", label: "Material", tooltip: "Optional material (.mat) — overrides the default sprite shader. Empty = default sprite rendering." })
  material: string = "";

  @field({ type: "color", label: "Color", tooltip: "Sprite tint color" })
  color: { r: number; g: number; b: number; a: number } = { r: 255, g: 255, b: 255, a: 255 };

  @field({ type: "color", label: "Emission", tooltip: "HDR emissive color (lit render paths). Values above 255 glow under bloom; black = no emission." })
  emission: { r: number; g: number; b: number; a: number } = { r: 0, g: 0, b: 0, a: 255 };

  @field({ type: "bool", label: "Flip X" })
  flipX: boolean = false;

  @field({ type: "bool", label: "Flip Y" })
  flipY: boolean = false;

  @field({ type: "f32", label: "Pivot X", min: 0, max: 1 })
  pivotX: number = 0.5;

  @field({ type: "f32", label: "Pivot Y", min: 0, max: 1 })
  pivotY: number = 0.5;

  @field({ type: "i32", label: "Sorting Layer" })
  sortingLayer: number = 1;

  @field({ type: "i32", label: "Order in Layer" })
  orderInLayer: number = 0;

  @field({ type: "f32", label: "Width", tooltip: "0 = auto from texture", min: 0, group: "Size" })
  width: number = 0;

  @field({ type: "f32", label: "Height", tooltip: "0 = auto from texture", min: 0, group: "Size" })
  height: number = 0;

  @field({ type: "f32", label: "Region U0", min: 0, max: 1, step: 0.01, group: "UV Region" })
  regionU0: number = 0;

  @field({ type: "f32", label: "Region V0", min: 0, max: 1, step: 0.01, group: "UV Region" })
  regionV0: number = 0;

  @field({ type: "f32", label: "Region U1", min: 0, max: 1, step: 0.01, group: "UV Region" })
  regionU1: number = 1;

  @field({ type: "f32", label: "Region V1", min: 0, max: 1, step: 0.01, group: "UV Region" })
  regionV1: number = 1;

  @field({ type: "bool", label: "Visible" })
  visible: boolean = true;

  // ──────────────────────────────────────────────────────────────────────────
  // Runtime API (Unity-style). "Cold" methods — NO base class: there is no
  // engine.* render/sprite global-services module, and these are the component's
  // OWN @field data. Methods just mutate that data; SpriteSync re-reads it every
  // frame (go.getComponentData('core.SpriteRenderer')) and pushes it into the SoA
  // sprite storage, so a mutation here shows up on the next rendered frame.
  // None of these names collide with an @field (so no serialized data is shadowed).
  // ──────────────────────────────────────────────────────────────────────────

  /** Set the tint color (0-255 per channel; alpha preserved if omitted). */
  setColor(r: number, g: number, b: number, a: number = this.color.a): void {
    this.color = { r, g, b, a };
  }

  /** Set the HDR emissive color (0-255 per channel; >255 = HDR glow). */
  setEmission(r: number, g: number, b: number): void {
    this.emission = { r, g, b, a: this.emission.a };
  }

  /** Set the tint alpha only (0-255). */
  setAlpha(a: number): void {
    this.color = { ...this.color, a };
  }

  /** Current tint alpha (0-255). */
  getAlpha(): number {
    return this.color.a;
  }

  /** Set the sprite texture asset id (empty string clears it). */
  setSprite(assetId: string): void {
    this.sprite = assetId;
  }

  /** Set the material asset id (empty string = default sprite rendering). */
  setMaterial(assetId: string): void {
    this.material = assetId;
  }

  /** Set horizontal/vertical flip flags. */
  setFlip(flipX: boolean, flipY: boolean): void {
    this.flipX = flipX;
    this.flipY = flipY;
  }

  /** Toggle render visibility. */
  setVisible(visible: boolean): void {
    this.visible = visible;
  }
}